package com.essntl.features.proposal.ui.addeditproposal

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import core.dateformat.utils.stringToLocalDateTime
import com.essntl.core.utils.viewmodel.State
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.file.ui.FileState
import com.essntl.features.paymentlink.domain.model.PaymentLinkModel
import com.essntl.features.price.domain.model.proposalserviceprice.ProposalServicePriceModel
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.mohamedrejeb.richeditor.model.RichTextState
import core.dateformat.utils.formatDateToYYYYMMDD
import core.dateformat.utils.fromDDTOMM
import kotlin.collections.map
import kotlin.collections.orEmpty

data class AddEditProposalState(
    val fileState: FileState = FileState(),
    val addEditProposal: State<ProposalModel> = State(),
    val selectedProposalServiceDate: String = "",
    val selectedProposalService: MutableState<ProposalServiceModel?> = mutableStateOf(null),

    val proposal: MutableState<ProposalModel> = mutableStateOf(ProposalModel.empty),
    val getProposal: State<ProposalModel> = State(),
    val getProposalHistory: State<ProposalModel?> = State(),
    val getProfile: State<ProfileModel> = State(),
    val updateTravelAgent: State<TravelAgentModel> = State(),
    val updateProposalDates: State<Unit> = State(),
    val publish: State<Unit> = State(),

    val getPaymentLinks: State<List<PaymentLinkModel>> = State(),
    val paymentLinks: MutableState<List<PaymentLinkModel>> = mutableStateOf(emptyList()),
    val proposalServiceList: List<ProposalServiceModel> = emptyList(),
    val getAllProposalService: State<List<ProposalServiceModel>> = State(),
    val getAllProposalServicesPrices: State<List<ProposalServicePriceModel>> = State(),
    val updateProposalServicesPrices: List<String> = emptyList(),
    val getAllProposalHistoryService: State<List<ProposalServiceModel>> = State(),
    val getAllCategory: State<List<CategoryModel>> = State(),
    val getAllArea: State<List<AreaModel>> = State(),
    val getAllClient: State<List<ClientModel>> = State(),
    val openAlertSaveProposalDialog: Boolean = false,
    val getCurrentProfile: State<ProfileModel> = State(),
    val getAllProfile: State<List<ProfileModel>> = State(),
    val getAllProposalTask: State<List<TaskModel>> = State(),
    val updateTaskLoading: Set<String> = emptySet(),
    val deleteTaskLoading: Set<String> = emptySet(),
) {
    val isLoading = getAllArea.isLoading ||
        getProposal.isLoading ||
        getAllProposalService.isLoading ||
        getAllProposalServicesPrices.isLoading

    val filteredProposalServices = proposalServiceList.filter { proposalServiceModel ->
        formatDateToYYYYMMDD(fromDDTOMM(proposalServiceModel.startDate)) in
            formatDateToYYYYMMDD(
                fromDDTOMM(
                    proposal.value.fromDate.orEmpty(),
                ),
            )..
            formatDateToYYYYMMDD(
                fromDDTOMM(
                    proposal.value.toDate.orEmpty(),
                ),
            )
    }

    val isEnabled = getProposal.data?.status != ProposalModel.Status.Confirmed
}

@Composable
fun AddEditProposalState.rememberPublishProposalEnabled(): Boolean =
    remember(
        getProposal.data, getProposalHistory.data,
        getAllProposalService.data, getAllProposalHistoryService.data,
    ) {
        if (getProposal.data?.status == ProposalModel.Status.Confirmed) return@remember false

        val proposalHistoryCreatedAt = stringToLocalDateTime(getProposalHistory.data?.createdAt) ?: return@remember true
        val proposalCreatedAt = stringToLocalDateTime(getProposal.data?.createdAt) ?: return@remember false
        val proposalUpdatedAt = stringToLocalDateTime(getProposal.data?.updatedAt) ?: proposalCreatedAt

        val proposalServiceList = getAllProposalService.data
            .orEmpty()
            .map {
                it.copy(
                    id = "",
                    createdAt = "",
                    updatedAt = null,
                    proposalId = null,
                    updatedBy = null,
                    editedDescription = RichTextState().setHtml(it.editedDescription ?: "").toHtml(),
                )
            }.sortedWith(
                compareBy<ProposalServiceModel> {
                    it.service.id
                }.thenBy {
                    it.index
                },
            )

        val proposalHistoryServiceList = getAllProposalHistoryService.data
            .orEmpty()
            .map {
                it.copy(
                    id = "",
                    createdAt = "",
                    updatedAt = null,
                    proposalId = null,
                    updatedBy = null,
                    editedDescription = RichTextState().setHtml(it.editedDescription ?: "").toHtml(),
                )
            }.sortedWith(
                compareBy<ProposalServiceModel> {
                    it.service.id
                }.thenBy {
                    it.index
                },
            )

        if (proposalServiceList != proposalHistoryServiceList) return@remember true

        proposalHistoryCreatedAt < proposalUpdatedAt
    }